// ToDo: WW-7459 Move to shared shadow folder
import styles from '../../../../../../erp/components/tab-panels/positions/components/edit-table/EditTable.module.scss';

import { Box } from '@mui/material';
import clsx from 'clsx';
import { Hooks, Row } from 'react-table';

import { HeaderCheckbox } from '@work4all/components/lib/dataDisplay/basic-table/components/header/components/header-checkbox/HeaderCheckbox';
import { SELECTION_COLUMN_ID } from '@work4all/components/lib/dataDisplay/basic-table/utils/makeRowsSelectable';
import { Checkbox } from '@work4all/components/lib/input/checkbox-radio/CheckboxRadio';

import { remToPx } from '@work4all/data/lib/hooks/useRemToPx';

import { ERP_EXPAND_COLUMN_ID } from '../../../../../../../../../../components/data-tables/generic-column/GenericColumns';

import { EditTableEntry } from './types';

interface UseCustomColumnModifierParams<T extends EditTableEntry> {
  onSelectedItemIdsChange?: (selectedItemIds: string[]) => void;
  showSelectionColumn?: boolean;
  rows?: Row<T>[]; // Or a more specific row type, e.g., Row[]
  decorators?: {
    getRelation?: (pos: T) => 'parent' | 'child' | 'normal';
    // Add other potential decorator functions here
  };
  hideCheckboxOnChildren?: boolean;
  showBorders?: boolean;
}

/**
 * Applies custom column modifications to the table hooks.
 * This function adds columns for row selection, expansion, and styling borders.
 * @param {Hooks} hooks - The React Table hooks object.
 * @param {UseCustomColumnModifierParams} params - The parameters for customizing column modifications.
 * @param {Function} [params.onSelectedItemIdsChange] - Callback for when selected item IDs change.
 * @param {boolean} [params.showSelectionColumn] - Toggles the visibility of the selection column.
 * @param {Array} [params.rows] - The table's row data.
 * @param {object} [params.decorators] - Decorator functions, e.g., for getting row relations.
 * @param {boolean} [params.hideCheckboxOnChildren] - If true, hides checkboxes on child rows.
 * @param {boolean} [params.showBorders] - Toggles a placeholder column for border styling.
 */
export function applyCustomTableHooks<T extends EditTableEntry>(
  hooks: Hooks<T>,
  params: UseCustomColumnModifierParams<T>
) {
  const {
    onSelectedItemIdsChange,
    showSelectionColumn,
    rows,
    decorators,
    hideCheckboxOnChildren,
    showBorders,
  } = params;
  hooks.visibleColumns.push((columns) => {
    const newColumns = [];

    // Add selection column if enabled
    if (onSelectedItemIdsChange && showSelectionColumn) {
      newColumns.push({
        id: SELECTION_COLUMN_ID,
        disableResizing: true,
        minWidth: 40,
        width: 40,
        maxWidth: 40,
        sticky: 'left',
        Header: ({ getToggleAllRowsSelectedProps }) => {
          const { checked, indeterminate, onChange, role } =
            getToggleAllRowsSelectedProps();
          return (
            <HeaderCheckbox
              checked={checked}
              indeterminate={indeterminate}
              onChange={(e, checked) => {
                const prevValue = rows.filter(
                  (x) => x.original.disableSelect && x.isSelected
                );
                onChange(e, checked);
                prevValue.forEach((x) => x.toggleRowSelected(x.isSelected));
              }}
              role={role}
            />
          );
        },
        Cell: ({ row }) => {
          const { checked, onChange, indeterminate, role } =
            row.getToggleRowSelectedProps();
          const relation = decorators?.getRelation
            ? decorators.getRelation(row.original)
            : 'normal';

          if (relation === 'child' && hideCheckboxOnChildren) {
            return null;
          }

          return (
            <div data-table-row-selectbox-marker={true}>
              <Checkbox
                disabled={row.original.disableSelect}
                className={clsx(styles.checkbox, {
                  [styles.checked]: checked,
                })}
                checked={checked}
                indeterminate={indeterminate}
                onChange={onChange}
                role={role}
              />
            </div>
          );
        },
      });
    }

    // Add expansion column if a relation decorator is provided
    if (decorators?.getRelation) {
      newColumns.push({
        id: ERP_EXPAND_COLUMN_ID,
        disableResizing: true,
        minWidth: 24,
        width: 24,
        maxWidth: 24,
        sticky: 'left',
        Header: () => (
          <Box bgcolor="var(--ui01)" right={0} left={0} position="absolute" />
        ),
        Cell: () => <Box />,
      });
    }

    const newColumnsEnd = [];
    // Add a placeholder column if borders are not shown
    if (!showBorders) {
      newColumnsEnd.push({
        id: 'PLACEHOLDER',
        disableResizing: true,
        minWidth: remToPx(3),
        width: remToPx(3),
        maxWidth: remToPx(3),
      });
    }

    return [...newColumns, ...columns, ...newColumnsEnd];
  });
}
